// chat/voice.tsx — FREE, on-device voice for the App Brain chat (Oriora fleet standard).
//   • speak-in  = 🎤 MicButton: Web Speech API SpeechRecognition (browser + macOS/iOS webview where
//                 supported). Self-hides only where the API is truly absent (older iOS webview), because
//                 there the system keyboard already dictates into the field.
//   • speak-out = 🔊 SpeakButton: speechSynthesis reads an AI reply aloud (the device's own TTS engine).
// Both run ENTIRELY on the device — nothing audio is sent to any server (zero extra egress; the text was
// already local). Self-contained + inline-styled (CSS-var fallbacks) so it drops into any app's chat
// with no stylesheet changes. `ovc` = Oriora Voice Chat.
import { useEffect, useRef, useState } from "react";

// ── speak-out: device text-to-speech ──────────────────────────────────────────
const synth: SpeechSynthesis | undefined =
  typeof window !== "undefined" ? window.speechSynthesis : undefined;

// Best free device voice (review finding: the engine default is the compact robotic one).
// Prefer Premium/Enhanced voices, then the known-good named ones, then any English. Free, on-device.
let bestVoice: SpeechSynthesisVoice | null | undefined;
function pickBestVoice(): SpeechSynthesisVoice | null {
  if (bestVoice !== undefined) return bestVoice;
  const en = (synth?.getVoices() ?? []).filter((v) => v.lang?.toLowerCase().startsWith("en"));
  bestVoice =
    en.find((v) => /premium/i.test(v.name)) ??
    en.find((v) => /enhanced/i.test(v.name)) ??
    ["Ava", "Samantha", "Serena", "Allison", "Zoe", "Kate"].map((n) => en.find((v) => v.name?.startsWith(n))).find(Boolean) ??
    en[0] ?? null;
  return bestVoice ?? null;
}
if (synth && "onvoiceschanged" in synth) {
  synth.addEventListener?.("voiceschanged", () => { bestVoice = undefined; });
}

const SPK: React.CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
  border: "1px solid var(--border, #d9d4ea)", background: "transparent",
  color: "var(--ink-2, #6b6580)", borderRadius: 999, padding: "3px 10px",
  fontSize: 12.5, fontWeight: 600, lineHeight: 1.4,
};

export function SpeakButton({ text }: { text: string }) {
  const [on, setOn] = useState(false);
  useEffect(() => () => { try { synth?.cancel(); } catch { /* ignore */ } }, []);
  if (!synth || !text.trim()) return null;
  const toggle = () => {
    try {
      if (on) { synth.cancel(); setOn(false); return; }
      synth.cancel();
      const u = new SpeechSynthesisUtterance(text);
      const v = pickBestVoice();
      if (v) u.voice = v;
      u.onend = () => setOn(false);
      u.onerror = () => setOn(false);
      setOn(true);
      synth.speak(u);
    } catch { setOn(false); }
  };
  return (
    <button type="button"
      style={on ? { ...SPK, color: "var(--accent, #6a5acd)", borderColor: "var(--accent, #6a5acd)" } : SPK}
      aria-label={on ? "Stop reading" : "Read aloud"} title={on ? "Stop" : "Read aloud"}
      onClick={toggle}>{on ? "■ Stop" : "🔊 Listen"}</button>
  );
}

// ── speak-in: device dictation ─────────────────────────────────────────────────
function getSR(): (new () => any) | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
}

const MIC: React.CSSProperties = {
  flexShrink: 0, width: 40, height: 40, borderRadius: 999, padding: 0, cursor: "pointer",
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  border: "1px solid var(--border, #d9d4ea)", background: "var(--elev-2, #ffffff)",
  color: "var(--ink-1, #1f1b2e)", fontSize: 18, lineHeight: 1,
  transition: "background .15s, color .15s, box-shadow .15s, border-color .15s",
};
const MIC_ON: React.CSSProperties = {
  background: "var(--accent, #6a5acd)", borderColor: "var(--accent, #6a5acd)",
  color: "#ffffff", boxShadow: "0 0 0 4px rgba(106,90,205,.20)",
};

export function MicButton({ onText, disabled }: { onText: (t: string) => void; disabled?: boolean }) {
  const [listening, setListening] = useState(false);
  // 🔴 G28 COROLLARY (2026-08-26): a swallowed permission denial IS the dead button.
  // The old handler only reset the button state and said
  // NOTHING, so a denied mic looked identical to "nothing happened". That is verbatim the
  // rejection we took: Guideline 2.1(a), "the microphone icon was unresponsive" (Content +
  // Dev, macOS, 2026-07-23). On the sandboxed Mac App Store build a denial is the NORMAL
  // outcome when the entitlement is absent, so silence there is guaranteed, not unlucky.
  const [micErr, setMicErr] = useState<string | null>(null);
  const recRef = useRef<any>(null);
  useEffect(() => () => { try { recRef.current?.stop(); } catch { /* ignore */ } }, []);
  const SR = getSR();
  if (!SR) return null; // older iOS webview: system keyboard mic already dictates → no dead button
  const toggle = () => {
    if (listening) { try { recRef.current?.stop(); } catch { /* ignore */ } setListening(false); return; }
    setMicErr(null);
    try {
      const rec = new SR();
      rec.lang = navigator.language || "en-US";
      rec.interimResults = false;
      rec.maxAlternatives = 1;
      rec.onresult = (e: any) => {
        const t = e?.results?.[0]?.[0]?.transcript;
        if (t) onText(String(t));
      };
      rec.onend = () => setListening(false);
      rec.onerror = (ev: any) => {
        setListening(false);
        // Say WHY. "no-speech"/"aborted" are ordinary (you stopped, or said nothing) — those
        // stay quiet; everything else gets a visible, actionable line.
        const code = ev?.error;
        if (code === "not-allowed" || code === "service-not-allowed")
          setMicErr("Microphone access is off for this app. Turn it on in Settings › Privacy & Security › Microphone, then try again.");
        else if (code === "audio-capture") setMicErr("No microphone was found on this device.");
        else if (code === "network") setMicErr("Dictation needs a network connection.");
        else if (code && code !== "no-speech" && code !== "aborted") setMicErr("Dictation stopped unexpectedly.");
      };
      recRef.current = rec;
      setListening(true);
      rec.start();
    } catch { setListening(false); setMicErr("Dictation could not start on this device."); }
  };
  return (
    <span style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <button type="button"
        style={listening ? { ...MIC, ...MIC_ON } : MIC}
        aria-label={listening ? "Stop dictation" : "Speak"} title={listening ? "Listening… tap to stop" : "Speak"}
        onClick={toggle} disabled={disabled}>{listening ? "■" : "🎤"}</button>
      {micErr && (
        <span role="alert" onClick={() => setMicErr(null)} style={{
          position: "absolute", bottom: "calc(100% + 6px)", right: 0, zIndex: 50, width: 232,
          background: "var(--elev-2, #ffffff)", color: "var(--ink-1, #1f1b2e)", cursor: "pointer",
          border: "1px solid var(--border, #d9d4ea)", borderRadius: 10, padding: "8px 10px",
          fontSize: 12, lineHeight: 1.35, textAlign: "left", boxShadow: "0 6px 20px rgba(0,0,0,.16)",
        }}>{micErr}</span>
      )}
    </span>
  );
}
